import DigiobcanApi from '../../api/DigiobcanApi';
import { createQueryFn, QueryNameSpace, useQ } from '../../lib/query';
import { useAuth } from '@presov.sk/presov-auth-react';
import type { QueryFunctionContext } from '@tanstack/react-query';
import React from 'react';

type Params = {
  userId: string;
};

export const meGetReportCategories = createQueryFn(
  QueryNameSpace.meGetReportCategories,
  async ({ queryKey }: QueryFunctionContext<[string] | [string, Params]>) => {
    const [, params] = queryKey;

    if (!params) {
      throw new Error('Provide required parameters!');
    }

    const { categories } = await DigiobcanApi.meGetReportCategories();

    return categories;
  },
);

/**
 * Smie prihlásený úradník v tejto kategórii **konať**, alebo ju len vidí?
 *
 * Od septembra 2026 to nie je tá istá otázka. Oprávnenie
 * `pruser:report-categories:read-all` dá držiteľovi (typicky sekretariátu)
 * celý zoznam podnetov na čítanie, ale zapisovať smie naďalej len v tých
 * kategóriách, ku ktorým je priradený. Ktoré to sú, hovorí príznak `managed`
 * pri každej položke - preto sa tá otázka nedá zodpovedať z toho, či je
 * kategória v zozname.
 *
 * Kým sa zoznam načítava, vracia `true`. Predvolená odpoveď „nesmieš" by
 * na okamih zhasla tlačidlá každému úradníkovi a to preblikne pri každom
 * otvorení detailu; opačným smerom sa nestane nič horšie, než že úradník
 * uvidí tlačidlo, ktoré API vzápätí odmietne 403 - a to je stav, ktorý
 * obrazovka musí zniesť tak či tak.
 */
export const useCanManageReportCategory = (): ((
  categoryId: string,
) => boolean) => {
  const { authUser } = useAuth();

  const { data: categories } = useQ(
    meGetReportCategories,
    { userId: authUser.clientAccountId },
    { staleTime: 5 * 60 * 1000 },
  );

  return (categoryId: string) => {
    if (!categories) {
      return true;
    }

    const category = categories.find((c) => c.category_id === categoryId);

    // Kategória, ktorá v zozname vôbec nie je, nie je „nespravovaná" - je to
    // stav, ktorý nastať nemá (nález v nej by sa nezobrazil). Nech sa
    // obrazovka správa ako predtým a rozhodne API.
    return category ? category.managed : true;
  };
};

/** Príznaky členstva prihláseného úradníka v kategóriách (schvaľovanie). */
type Membership = {
  manager: boolean;
  canApprove: boolean;
  requiresApproval: boolean;
  /**
   * Smie v kategórii prideľovať nálezy (`can_assign`, správca, `access-all`).
   * Server ho už zložil, nie je to holý príznak členstva.
   */
  canAssign: boolean;
};

const NO_MEMBERSHIP: Membership = {
  manager: false,
  canApprove: false,
  requiresApproval: false,
  canAssign: false,
};

/**
 * Zoznam kategórií prihláseného úradníka pre hooky nižšie. Ten istý dotaz
 * a ten istý `staleTime` ako `useCanManageReportCategory`, takže sa nepýta
 * druhýkrát.
 */
const useMyReportCategories = () => {
  const { authUser } = useAuth();

  // Volá sa aj z navigácie, ktorá sa kreslí aj neprihlásenému - `authUser`
  // tam nie je a dotaz sa nesmie spustiť (API by aj tak vrátilo 401).
  const { data } = useQ(
    meGetReportCategories,
    { userId: authUser?.clientAccountId ?? '' },
    { staleTime: 5 * 60 * 1000, enabled: !!authUser },
  );

  return data;
};

/**
 * Rola prihláseného úradníka v kategórii - správca, schvaľovateľ, alebo
 * potrebuje schválenie. Príznaky sú na **členstve**, takže mimo neho sú vždy
 * `false`; `access-all` ich nedáva (viď `lib/reports/approval` v digiobcan-api).
 *
 * Na rozdiel od `useCanManageReportCategory` sa kým sa zoznam načítava,
 * vracia „nič": obrazovka sa správa ako doteraz a pravdu povie API.
 */
export const useReportCategoryMembership = (): ((
  categoryId: string,
) => Membership) => {
  const categories = useMyReportCategories();

  return (categoryId: string) => {
    const category = categories?.find((c) => c.category_id === categoryId);

    return category
      ? {
          manager: category.manager,
          canApprove: category.can_approve,
          requiresApproval: category.requires_approval,
          canAssign: category.can_assign,
        }
      : NO_MEMBERSHIP;
  };
};

/**
 * Schvaľuje prihlásený úradník aspoň v jednej kategórii (alebo ju spravuje)?
 * Podľa toho sa v zozname podnetov ponúka filter „Na schválenie".
 */
export const useIsApproverSomewhere = (): boolean => {
  const categories = useMyReportCategories();

  return !!categories?.some((c) => c.can_approve || c.manager);
};

/**
 * Podliehajú vyjadrenia prihláseného schváleniu aspoň v jednej kategórii?
 * Len vtedy mu chodia správy o rozhodnutiach o jeho vyjadreniach - podľa
 * toho sa v nastaveniach notifikácií ponúka ich prepínač.
 */
export const useRequiresApprovalSomewhere = (): boolean => {
  const categories = useMyReportCategories();

  return !!categories?.some((c) => c.requires_approval);
};

/**
 * Je prihlásený úradník aspoň v jednej kategórii, ktorá funguje na
 * prideľovaní (moj-presov#17)? Len tam má zmysel filter „Pridelené mne" -
 * inde sa nálezy nikomu neprideľujú.
 */
export const useIsInAssigningCategory = (): boolean => {
  const categories = useMyReportCategories();

  return !!categories?.some((c) => c.assignment);
};

/**
 * Smie prihlásený úradník prideľovať nálezy aspoň v jednej kategórii
 * s prideľovaním? `can_assign` skladá server (príznak člena, správca,
 * `access-all`). Len taký vidí nálezy kategórie všetkých riešiteľov, takže
 * len jemu dáva zmysel filter „Riešiteľ" - obyčajný riešiteľ vidí iba svoje
 * a na to má „Pridelené mne".
 */
export const useCanAssignSomewhere = (): boolean => {
  const categories = useMyReportCategories();

  return !!categories?.some((c) => c.assignment && c.can_assign);
};

/**
 * Kategórie, ktoré prihlásený úradník spravuje. Správca smie meniť ich členov
 * bez celomestského `pruser:report-categories:users:manage`.
 */
export const useManagedReportCategoryIds = (): string[] => {
  const categories = useMyReportCategories();

  return React.useMemo(
    () => (categories ?? []).filter((c) => c.manager).map((c) => c.category_id),
    [categories],
  );
};

/**
 * Spravuje prihlásený úradník aspoň jednu kategóriu? `undefined`, kým sa
 * zoznam načítava - guard routy na to musí počkať, inak by správcu presmeroval
 * preč skôr, než sa dozvie, že tam patrí.
 */
export const useIsCategoryManagerSomewhere = (): boolean | undefined => {
  const categories = useMyReportCategories();

  return categories ? categories.some((c) => c.manager) : undefined;
};
